import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { COMPANY, SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * Outstanding items for legal sign-off are tracked in
 * docs/internal-pre-launch-checklist.md: exact retention periods, named
 * processors, contact-matching implementation, minimum age, and international
 * transfer wording. Do not surface those uncertainties to visitors.
 */
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-543" data-mp="198mhrwp6jzac">
      <SEO title="Privacy Policy | KittyPot" description="How KittyPot Ltd collects, uses and protects your personal data in the KittyPot app and on kittypot.com, written in plain UK English." ogTitle="KittyPot Privacy Policy" ogDescription="How KittyPot handles your personal data." path="/privacy-policy" data-id="element-544" data-mp="157u5eul0he9u" />
      <Navbar data-id="element-545" data-mp="a0nfd6bpp26b" />
      <main className="pt-32 pb-24" data-id="element-546" data-mp="9z2llix81n9i">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-547" data-mp="a26xz3kubpb9">
          <h1 className="text-4xl font-black text-kitty-dark mb-4" data-id="element-548" data-mp="3rfkkf4nzbl99">
            Privacy Policy
          </h1>
          <p className="text-gray-500 font-medium mb-10" data-id="element-549" data-mp="8zsliq3o9b6">
            Last updated: 26 August 2026
          </p>

          <div className="prose prose-gray max-w-none text-gray-600 space-y-6" data-id="element-550" data-mp="3iema64my6hi8">
            <p data-id="element-551" data-mp="3nfs4g6w9actv">
              This policy explains what personal data {COMPANY.legalName}{' '}
              collects when you use the KittyPot app or our website, why we
              collect it, and what rights you have. It is written in plain UK
              English.
            </p>

            <p data-id="element-552" data-mp="16rg0wjhezmfh">
              KittyPot helps groups record shared expenses, split costs and keep
              track of who owes what. KittyPot does not hold or move your money.
              Group members settle up directly with each other.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-553" data-mp="1597l84ujc93p">
              1. Who we are
            </h2>
            <p data-id="element-554" data-mp="109l6ts2z0mtb">
              {COMPANY.legalName} (company number {COMPANY.registrationNumber})
              is the data controller for the personal data described in this
              policy. You can reach us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-555" data-mp="1sxquonpbmrxd">
                {SUPPORT_EMAIL}
              </a>{' '}
              or by post:
            </p>
            <address className="not-italic" data-id="element-556" data-mp="1f6c8y9414cmm">
              {COMPANY.addressLines.map(line => <React.Fragment key={line} data-id="element-557" data-mp="2bqbgjl6lrwro">
                  {line}
                  <br data-id="element-558" data-mp="26ytesuwr9s5n" />
                </React.Fragment>)}
            </address>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-559" data-mp="3plb8szih9ph7">
              2. Account information
            </h2>
            <p data-id="element-560" data-mp="3ujmina8rvzzf">
              When you create a KittyPot account we process the information
              needed to identify you and keep your account secure. Depending on
              how you sign up, this may include:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-561" data-mp="7gzu8b794l07">
              <li data-id="element-562" data-mp="cfkuubqf16p6">your name or display name</li>
              <li data-id="element-563" data-mp="ccowwnekii0q">your email address</li>
              <li data-id="element-564" data-mp="hdcjmwvrkcp5">a phone number, where you choose to provide one</li>
              <li data-id="element-565" data-mp="hbjejm0su0j9">a profile picture, where you choose to add one</li>
              <li data-id="element-566" data-mp="m54cxf6b0wiz">
                authentication and account identifiers, including identifiers
                supplied by Google or Facebook if you sign in with them
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-567" data-mp="v73vwd0vqaok">
              3. Kitty and expense information
            </h2>
            <p data-id="element-568" data-mp="mhdf76tty5v5">
              To provide the core service we process the content you and other
              members add to a kitty:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-569" data-mp="hi0tipep34u5">
              <li data-id="element-570" data-mp="w76pm4i8kext">kitty names and the members of each kitty</li>
              <li data-id="element-571" data-mp="w9j4g8sp696n">expenses, including description, amount and date</li>
              <li data-id="element-572" data-mp="r9ocs313lfkj">who paid, and who an expense is shared with</li>
              <li data-id="element-573" data-mp="3i97c1e0yi52">split information, whether equal or custom amounts</li>
              <li data-id="element-574" data-mp="3kly941rg2u9">balances between members</li>
              <li data-id="element-575" data-mp="3uq5aq9blc64t">
                settlement records, including amounts marked as paid and
                confirmations that a payment was received
              </li>
            </ul>
            <p data-id="element-576" data-mp="8hcbljm6kkbz">
              This information is shared with the other members of that kitty,
              because a shared expense record only works if the group can see
              it. It is not made public.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-577" data-mp="1jwiwuyu06cxh">
              4. Contact matching
            </h2>
            <p data-id="element-578" data-mp="1rimnkaq87gbq">
              If you give the app permission to access your contacts, we use
              that access only to check whether people you already know are on
              KittyPot, so you can invite them more easily. Contact identifiers
              may be submitted to KittyPot for that matching.
            </p>
            <p data-id="element-579" data-mp="2a7b4klll91i8">
              We do not build a public directory, and we do not let anyone
              browse a list of all KittyPot users. You can use KittyPot without
              granting contacts permission, and you can withdraw the permission
              at any time in your device settings.
            </p>
            {/* INTERNAL: confirm whether contact identifiers are hashed and
                discarded after matching, then describe that precisely here. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-580" data-mp="2t42maqlhf8k6">
              5. Receipts
            </h2>
            <p data-id="element-581" data-mp="37scf6eqbaymu">
              You can optionally attach a receipt image to an expense. Where
              receipt scanning is available, an image may be processed to read
              details such as the amount so they can be suggested when you add
              the expense. Receipt images and any details taken from them are
              stored as part of the expense record so the group can refer back
              to them.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-582" data-mp="2ovb72l1goieo">
              6. Bank details
            </h2>
            <p data-id="element-583" data-mp="2k0xcgkumifb2">
              You may choose to save bank details, such as an account name, sort
              code and account number, so they are easy to share when your group
              settles up. This is entirely optional. We store these details so
              that you can share them, and we do not publish them, sell them or
              share them automatically without you choosing to.
            </p>
            <p data-id="element-584" data-mp="1r08xl5eq5jke">
              KittyPot does not use these details to make payments. Any payment
              between group members happens directly between you, outside
              KittyPot.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-585" data-mp="20yohanbf9qjv">
              7. Currency preferences
            </h2>
            <p data-id="element-586" data-mp="25wgdvun0i0ue">
              Where you record expenses in more than one currency, we process
              your currency preferences and any conversion figures you supply so
              amounts can be displayed consistently in your kitty. Converted
              amounts shown in the app are for presentation and may not match a
              bank or provider rate at the time you settle.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-587" data-mp="1mzf7q6hq1uov">
              8. Device information and notifications
            </h2>
            <p data-id="element-588" data-mp="uico70ngiyv5">
              To deliver and support the app we may process technical
              information such as your device type and operating system, the app
              version you are running, and a push notification token if you turn
              notifications on. We also process basic diagnostic information so
              we can find and fix problems.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-589" data-mp="2ikmf14tvocgy">
              9. Reporting an issue
            </h2>
            <p data-id="element-590" data-mp="2rn5c18azfuva">
              If you report a problem, we process what you send us: your
              description of the issue, any screenshot you choose to attach, and
              diagnostic details such as your internal user identifier, device
              type, app version and error information. We use this only to
              investigate and resolve the issue.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-591" data-mp="3bbef4de2dl9k">
              10. How you found us, and location
            </h2>
            <p data-id="element-592" data-mp="36duv0wuoqe9p">
              We may record privacy-friendly information about how an account
              was created, such as an invite or campaign code, the registration
              source, and the platform or store you installed from. This helps
              us understand which routes into KittyPot work well. None of this
              involves tracking where you are.
            </p>
            <p data-id="element-593" data-mp="3kxyv0gc42nli">
              Our website runs no location-tracking or analytics scripts. Where
              the app uses a device permission such as location, your device
              will always ask you first, and you can review or withdraw any
              permission at any time in your device settings.
            </p>
            <p data-id="element-594" data-mp="2x7l2kopm6mqe">
              Like any internet service, our systems receive your IP address
              when your device connects to us. It is used to deliver the service
              and to keep it secure. An IP address can indicate an approximate
              region, such as a country or city, and where it is used in that
              way it is only ever at that coarse level, never a precise
              position.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-595" data-mp="3ezcqa4ipajia">
              11. Analytics
            </h2>
            <p data-id="element-596" data-mp="302lvk8jz1vz0">
              Where we use analytics, it is to understand how KittyPot is used
              in aggregate, such as which features are used and where people
              encounter problems, so we can improve the product. Our website
              currently runs no analytics or advertising scripts at all; the
              technologies it does use are set out in our Cookie Policy.
            </p>
            {/* INTERNAL: name the analytics providers actually in use once
                confirmed. The website itself currently loads none. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-597" data-mp="fjy7joiisflm">
              12. Why we process your data, and our lawful bases
            </h2>
            <ul className="list-disc pl-5 space-y-2" data-id="element-598" data-mp="kbpnz67yp4y1">
              <li data-id="element-599" data-mp="250a53oqhxgsg">
                <strong data-id="element-600" data-mp="24zs5qj8jemgb">To provide the service</strong>, creating your account,
                running your kitties, recording expenses, balances and
                settlements. Lawful basis: performance of a contract with you.
              </li>
              <li data-id="element-601" data-mp="2esfxgzknu086">
                <strong data-id="element-602" data-mp="29rsb4r7dm7ka">To keep the service secure and working</strong>,
                diagnostics, abuse prevention and fixing faults. Lawful basis:
                our legitimate interests in running a secure, reliable service.
              </li>
              <li data-id="element-603" data-mp="1h4fn0clkhrov">
                <strong data-id="element-604" data-mp="1h4z1tkfx7zyo">To improve KittyPot</strong>, aggregate analytics and
                understanding how features are used. Lawful basis: our
                legitimate interests, or your consent where required for the
                technology used.
              </li>
              <li data-id="element-605" data-mp="36g80onahkgxe">
                <strong data-id="element-606" data-mp="36jvttlc0r1bl">Contact matching, notifications and receipts</strong>,
                optional features you switch on. Lawful basis: your consent,
                given through the permission or setting.
              </li>
              <li data-id="element-607" data-mp="2444rfys2jgbj">
                <strong data-id="element-608" data-mp="243lgj20u5s70">Product and launch emails you asked for</strong>,
                waitlist and product updates. Lawful basis: your consent.
              </li>
              <li data-id="element-609" data-mp="2d52v2fylhltl">
                <strong data-id="element-610" data-mp="2d5kzw0zbp8d4">Meeting our legal obligations</strong>, for example
                responding to a lawful request. Lawful basis: legal obligation.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-611" data-mp="1ldnaerglm960">
              13. Service providers
            </h2>
            <p data-id="element-612" data-mp="1pefllq916o4x">
              We use trusted service providers to operate KittyPot, for example
              to host our systems, deliver notifications and provide analytics.
              They process personal data on our instructions and under
              appropriate contractual terms, and they may not use it for their
              own purposes.
            </p>
            <p data-id="element-613" data-mp="u64ypvcu4o3q">
              One provider is worth naming specifically. If you sign up to our
              waitlist on this website, your name, email address and marketing
              choice are submitted through Google infrastructure, which we use
              to collect and store waitlist entries. Google processes that
              submission on our behalf so we can contact you about the launch.
            </p>
            {/* INTERNAL: list the actual processors and their roles once
                confirmed. No providers are named that we cannot verify. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-614" data-mp="6ezl7muda7wl">
              14. International transfers
            </h2>
            <p data-id="element-615" data-mp="3tm1wep7gjuxn">
              Some of our service providers may process data outside the United
              Kingdom. Where that happens, we will put in place the safeguards
              required by UK data protection law so that your data continues to
              receive an appropriate level of protection.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-616" data-mp="1nliurrp0pm3r">
              15. How long we keep data
            </h2>
            <p data-id="element-617" data-mp="1sl3rdvgptidb">
              We keep personal data only for as long as we need it for the
              purpose it was collected for, or for as long as we are required to
              keep it by law. Kitty and expense records are kept while the kitty
              is in use and afterwards so that members can refer back to a
              shared history. When data is no longer needed, we delete or
              anonymise it.
            </p>
            {/* INTERNAL: set and state specific retention periods for
                accounts, kitty history, receipts and diagnostics. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-618" data-mp="zwprfa62gbz4">
              16. Deleting your account
            </h2>
            <p data-id="element-619" data-mp="2u8e8pizrsqmo">
              You can delete your account from within the app, or by emailing us
              from the address on your account. When you do:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-620" data-mp="ij6mjqdbmyv4">
              <li data-id="element-621" data-mp="dn985plmc2kz">
                we remove or anonymise your account information where it is
                appropriate to do so
              </li>
              <li data-id="element-622" data-mp="scxe544qvpf1">
                limited shared records may need to remain, in an anonymised
                form, so that other members' shared expense history and balances
                stay accurate
              </li>
              <li data-id="element-623" data-mp="wd7fu4li7to3">
                we may retain limited information where we are required to by
                law
              </li>
            </ul>
            <p data-id="element-624" data-mp="1b96vq1euuy3s">
              Anything no longer needed for those purposes is deleted or
              anonymised. We cannot remove an expense from someone else's
              records where doing so would make their shared history wrong. If
              you would like to know what remains after deletion, email us and
              we will explain.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-625" data-mp="1m1h4hrdillq">
              17. Your rights
            </h2>
            <p data-id="element-626" data-mp="5n3fyxhdovn6">Under UK data protection law you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-627" data-mp="akmwk41jtkia">
              <li data-id="element-628" data-mp="ao0zbde4jvq9">ask for a copy of the personal data we hold about you</li>
              <li data-id="element-629" data-mp="fm2hzx7n1t17">ask us to correct information that is wrong</li>
              <li data-id="element-630" data-mp="kisxaibmcklk">ask us to delete your data, subject to section 16</li>
              <li data-id="element-631" data-mp="ol7fr1bdlicb">ask us to restrict how we use your data</li>
              <li data-id="element-632" data-mp="ojmnjicjabhm">
                object to processing we carry out on the basis of legitimate
                interests
              </li>
              <li data-id="element-633" data-mp="yazabvjh6bgs">
                ask for your data in a portable format, where that right applies
              </li>
              <li data-id="element-634" data-mp="13b50f7a3jpwr">
                withdraw your consent at any time where we rely on consent
              </li>
            </ul>
            <p data-id="element-635" data-mp="189yfh4517oz1">
              To exercise any of these, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-636" data-mp="2srr01tge6opn">
                {SUPPORT_EMAIL}
              </a>
              . If you are unhappy with how we have handled your data you can
              complain to the Information Commissioner's Office at ico.org.uk.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-637" data-mp="3bgnnpehaaiyw">
              18. Children
            </h2>
            <p data-id="element-638" data-mp="36jnnhk748afp">
              KittyPot is intended for people who are old enough to use the
              service lawfully under the rules that apply to them, and it is not
              designed for children. We do not knowingly collect personal data
              from children. If you believe a child has created an account,
              contact us and we will look into it.
            </p>
            {/* INTERNAL: a specific minimum age is a business decision. Once
                set, state it here and make sure it matches the age rating
                declared on the App Store and Google Play. */}

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-639" data-mp="32mq4la0ekxij">
              19. Security
            </h2>
            <p data-id="element-640" data-mp="2tsbrne3chz2l">
              We take reasonable technical and organisational measures to
              protect your data, including encrypted connections and access
              controls. No online service can promise perfect security, so we do
              not claim to be completely secure, but we work to keep your data
              safe and to respond quickly if something goes wrong.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-641" data-mp="3cijoxfkd5vav">
              20. Changes to this policy
            </h2>
            <p data-id="element-642" data-mp="1w41pmz3k7bbh">
              We may update this policy as KittyPot develops. When we make a
              material change we will update the date at the top of this page
              and, where appropriate, let you know in the app.
            </p>

            <h2 className="text-2xl font-bold text-kitty-dark mt-10 mb-4" data-id="element-643" data-mp="1olafmbd0i6yk">
              21. Contact us
            </h2>
            <p data-id="element-644" data-mp="22lbj7yknvshq">
              For any privacy question, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-645" data-mp="22ip9pte7n2wv">
                {SUPPORT_EMAIL}
              </a>{' '}
              or write to us at the address in section 1.
            </p>
          </div>
        </div>
      </main>
      <Footer data-id="element-646" data-mp="1jns54gjcjjog" />
    </div>;
}